import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  ChevronLeft,
  ExternalLink,
  LoaderCircle,
  Search,
  Send,
  Sparkles,
  Truck,
  Pencil,
  CheckCircle2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  marketDisplayText,
  marketDriverDisplayItem,
  normalizeMarketLanguage,
} from "../utils/driverMarketCyrillic.mjs";
import {
  createDriverSavedFreightSearch,
  checkDriverSavedFreightSearch,
  createDriverAvailableVehicle,
  getDriverAvailableVehicles,
  getReisorAiQuota,
  getReisorAiResultSet,
  sendReisorAiMessage,
} from "../services/reisorAiTransport.js";

const RESULT_PAGE_SIZE = 20;
const MAX_PERSISTED_MESSAGES = 20;

function aiStorageKey(driverId) {
  return `reisor_driver_ai_v03b_${String(driverId || "anon")}`;
}

function makeId(prefix = "m") {
  try {
    if (globalThis.crypto?.randomUUID) return `${prefix}_${globalThis.crypto.randomUUID()}`;
  } catch {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function readPersisted(driverId) {
  try {
    const raw = localStorage.getItem(aiStorageKey(driverId));
    const parsed = raw ? JSON.parse(raw) : {};
    const rows = Array.isArray(parsed?.messages) ? parsed.messages : [];
    return {
      conversationId: String(parsed?.conversationId || ""),
      messages: rows
        .filter((row) => row && ["user", "assistant"].includes(row.role) && String(row.text || "").trim())
        .slice(-MAX_PERSISTED_MESSAGES)
        .map((row) => ({
          id: makeId("restore"),
          role: row.role,
          text: String(row.text || "").slice(0, 2400),
        })),
    };
  } catch {
    return { conversationId: "", messages: [] };
  }
}

function persistState(driverId, conversationId, messages) {
  try {
    const compactMessages = (Array.isArray(messages) ? messages : [])
      .filter((row) => row && ["user", "assistant"].includes(row.role) && String(row.text || "").trim())
      .slice(-MAX_PERSISTED_MESSAGES)
      .map((row) => ({ role: row.role, text: String(row.text || "").slice(0, 2400) }));
    localStorage.setItem(
      aiStorageKey(driverId),
      JSON.stringify({ conversationId: String(conversationId || ""), messages: compactMessages })
    );
  } catch {}
}

function localeFor(language) {
  const lng = normalizeMarketLanguage(language);
  if (lng === "tr") return "tr-TR";
  if (lng === "uz") return "uz-UZ";
  return "ru-RU";
}

function weightText(kg, locale, tonShort) {
  const number = Number(kg);
  if (!Number.isFinite(number) || number <= 0) return "—";
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(number / 1000)} ${tonShort}`;
}

function dateText(value, locale) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const d = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

function truckText(types, t) {
  const list = Array.isArray(types) ? types : [];
  const names = list
    .map((raw) => String(raw || "").trim().toUpperCase())
    .filter(Boolean)
    .slice(0, 8)
    .map((code) => t(`freightMarket.truckCodes.${code}`, { defaultValue: code }));
  return names.length ? names.join(" · ") : "—";
}

function savedSearchCriteria(spec = {}) {
  return {
    from: String(spec?.from || "").trim(),
    to: String(spec?.to || "").trim(),
    truck: String(spec?.truck || "").trim().toUpperCase(),
    // Persistent notification watch is route + body; one-off date remains an AI-search filter.
    loadingDate: "",
  };
}

function canSaveSearch(spec = {}) {
  const criteria = savedSearchCriteria(spec);
  return Boolean(criteria.from && criteria.to && criteria.truck);
}

function savedSearchDropsExtraFilters(spec = {}) {
  return Boolean(
    Boolean(spec?.loadingDate) ||
      spec?.weightMinTons != null ||
      spec?.weightMaxTons != null ||
      spec?.priceMin != null ||
      spec?.priceMax != null ||
      spec?.freshnessHours != null ||
      spec?.onlyWithPrice
  );
}

function normalizedVehicleBody(value = "") {
  const raw = String(value || "").trim().toUpperCase();
  const aliases = {
    REF: "REFRIGERATOR",
    REEFER: "REFRIGERATOR",
    BOX: "VAN",
    PLATFORM: "FLATBED",
    "ТЕНТ": "TENT",
    "РЕФ": "REFRIGERATOR",
    "РЕФРИЖЕРАТОР": "REFRIGERATOR",
    "ИЗОТЕРМ": "ISOTHERM",
    "ФУРГОН": "VAN",
    "КОНТЕЙНЕР": "CONTAINER",
    "ПЛАТФОРМА": "FLATBED",
  };
  return aliases[raw] || raw;
}

function foldRoute(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function explicitCapacityFromText(value = "") {
  const text = String(value || "");
  const m = text.match(/(?:^|\s)(\d{1,3}(?:[.,]\d+)?)\s*(?:т(?:онн(?:а|ы)?)?|t(?:on(?:na)?)?)(?=\s|$|[.,;:!?])/iu);
  if (!m) return null;
  const n = Number(String(m[1]).replace(",", "."));
  return Number.isFinite(n) && n > 0 && n <= 1000 ? n : null;
}


function buildReturnVehicleDraft({ spec = {}, sourceText = "", driver = {}, ownVehicles = [] } = {}) {
  const from = String(spec?.from || "").trim();
  const to = String(spec?.to || "").trim();
  const bodyType = normalizedVehicleBody(spec?.truck || driver?.bodyType || "");
  const explicitCapacity = explicitCapacityFromText(sourceText);
  const recentSameBody = (Array.isArray(ownVehicles) ? ownVehicles : [])
    .filter((item) => normalizedVehicleBody(item?.bodyType) === bodyType && Number(item?.capacityTons) > 0)
    .sort((a, b) => new Date(b?.updatedAt || b?.createdAt || 0).getTime() - new Date(a?.updatedAt || a?.createdAt || 0).getTime())[0] || null;
  const requestedCapacity = Number(spec?.capacityTons);
  const capacityTons = (Number.isFinite(requestedCapacity) && requestedCapacity > 0 ? requestedCapacity : null) || explicitCapacity || (recentSameBody ? Number(recentSameBody.capacityTons) : null);
  const duplicate = (Array.isArray(ownVehicles) ? ownVehicles : []).find((item) =>
    String(item?.status || "").toUpperCase() === "ACTIVE" &&
    foldRoute(item?.from) === foldRoute(from) &&
    foldRoute(item?.to) === foldRoute(to) &&
    normalizedVehicleBody(item?.bodyType) === bodyType &&
    Math.abs(Number(item?.capacityTons) - Number(capacityTons)) < 0.001
  ) || null;
  return {
    __aiDraft: true,
    from,
    to,
    bodyType,
    capacityTons,
    volumeM3: recentSameBody?.volumeM3 ?? null,
    partialLoad: false,
    priceMode: "NEGOTIABLE",
    priceAmount: null,
    currency: "UZS",
    comment: "",
    duplicate,
    ready: Boolean(from && to && bodyType && capacityTons),
    missingCapacity: !capacityTons,
  };
}

function friendlyError(error, t) {
  const code = String(error?.code || error?.message || "").toUpperCase();
  const status = Number(error?.status || 0);
  if (code === "AI_DAILY_LIMIT_REACHED") {
    return t("reisorAi.errors.dailyLimit", { defaultValue: "Лимит 50/50 исчерпан. Следующие 50 вопросов будут доступны через 24 часа." });
  }
  if (status === 429 || code.includes("429") || code.includes("RATE_LIMIT")) {
    return t("reisorAi.errors.rateLimit", { defaultValue: "Слишком много запросов. Попробуйте чуть позже." });
  }
  if (code === "AI_ACCESS_NOT_ELIGIBLE" || code === "AI_ROLE_NOT_ENABLED" || status === 403) {
    return t("reisorAi.errors.access", { defaultValue: "REISOR AI пока недоступен для этого аккаунта." });
  }
  if (code === "AI_RESULT_SET_EXPIRED" || code === "AI_RESULT_SET_NOT_FOUND") {
    return t("reisorAi.errors.resultExpired", { defaultValue: "Этот результат уже устарел. Выполните поиск ещё раз." });
  }
  if (code.includes("AI_GIGACHAT") || code === "AI_PROVIDER_DISABLED" || status === 503) {
    return t("reisorAi.errors.provider", { defaultValue: "AI временно недоступен. Попробуйте ещё раз через несколько секунд." });
  }
  if (status === 401 || code === "AI_AUTH_REQUIRED") {
    return t("reisorAi.errors.auth", { defaultValue: "Сессия REISOR истекла. Войдите снова." });
  }
  return t("reisorAi.errors.generic", { defaultValue: "Внутренний сервис временно не ответил. Попробуйте ещё раз через несколько секунд." });
}

function FreightPreviewCard({ item, language, onOpen }) {
  const { t } = useTranslation();
  const locale = localeFor(language);
  const display = marketDriverDisplayItem(item, language) || {};
  const cargo = marketDisplayText(String(display?.cargo || ""), language) || "—";
  const rate = String(display?.ratePrimary || display?.rateSecondary || "").trim() || t("freightMarket.negotiable", { defaultValue: "Договорная" });
  const loading = dateText(display?.loadingDate, locale) || (display?.loadingReady ? t("freightMarket.ready", { defaultValue: "Готов к загрузке" }) : "—");
  return (
    <div
      data-reisor-progressive-freight-card="v1"
      data-reisor-ai-freight-card="v03b"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/90 p-4 shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-black leading-snug text-white">
            {String(display?.from || "—")} → {String(display?.to || "—")}
          </div>
          <div className="mt-1 text-xs font-semibold text-slate-400">
            {t("freightMarket.loadingDate", { defaultValue: "Загрузка" })}: {loading}
          </div>
        </div>
        <div className="max-w-[145px] text-right text-[18px] font-black leading-tight text-slate-100">
          {rate}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <div className="text-slate-400">{t("freightMarket.cargo", { defaultValue: "Груз" })}</div>
        <div className="text-right font-semibold text-slate-100">{cargo}</div>
        <div className="text-slate-400">{t("freightMarket.weight", { defaultValue: "Вес" })}</div>
        <div className="text-right font-semibold text-slate-100">
          {weightText(display?.weightKg, locale, t("freightMarket.tonShort", { defaultValue: "т" }))}
        </div>
        <div className="text-slate-400">{t("profile.bodyType", { defaultValue: "Тип кузова" })}</div>
        <div className="text-right font-semibold text-slate-100">{truckText(display?.truckTypes, t)}</div>
      </div>
      <button
        type="button"
        onClick={() => onOpen?.(display?.id)}
        className="mt-3 inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-sm font-black text-white active:bg-blue-700"
      >
        <ExternalLink className="h-4 w-4" />
        {t("reisorAi.openFreight", { defaultValue: "Открыть груз" })}
      </button>
    </div>
  );
}

export default function DriverAiAssistant({
  open,
  onClose,
  driverToken,
  driverId,
  driver = null,
  onOpenFreight,
  onShowInMarket,
  onSaveSearch,
  onCheckSavedSearch,
  onPrepareVehicle,
  onPrepareFreight,
  onOpenCreateFreight,
  onOpenVehicleMarket,
  onOpenMyFreights,
  entryIntent = "",
  sendMessageRequest = sendReisorAiMessage,
  getResultSetRequest = getReisorAiResultSet,
  getQuotaRequest = getReisorAiQuota,
  surface = "driver",
}) {
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "ru").split("-")[0];
  const isExpeditorSurface = surface === "expeditor";
  const isCargoSurface = surface === "cargo";
  const cargoUi = useMemo(() => {
    if (language === "uz") {
      return {
        subtitle: "Юк эгаси учун AI ёрдамчи",
        greeting: "Юкни оддий сўзлар билан ёзинг: қаердан, қаерга, нима юк, вазни, кузови ва санаси. Мен қоралама тайёрлайман. Эълон фақат сиз текшириб тасдиқлагандан кейин жойланади.",
        createFreight: "Юк жойлаштириш",
        findCarrier: "Ташувчи топиш",
        myFreights: "Менинг юкларим / жавоблар",
        placeholder: "Масалан: Тошкент Алматы 18т ускуна, тент",
        draftTitle: "Юк қораламаси",
        openDraft: "Очиш ва текшириш",
      };
    }
    if (language === "tr") {
      return {
        subtitle: "Yük sahibi için AI yardımcısı",
        greeting: "Yükünüzü normal cümlelerle yazın: nereden, nereye, yük türü, ağırlık, araç tipi ve tarih. Bir taslak hazırlayacağım. Yayın yalnızca siz kontrol edip onayladıktan sonra yapılır.",
        createFreight: "Yük yayınla",
        findCarrier: "Taşıyıcı bul",
        myFreights: "Yüklerim / yanıtlar",
        placeholder: "Örnek: Taşkent Almatı 18t ekipman, tenteli",
        draftTitle: "Yük taslağı",
        openDraft: "Aç ve kontrol et",
      };
    }
    return {
      subtitle: "AI-помощник грузовладельца",
      greeting: "Опишите груз обычными словами: откуда, куда, что везём, вес, кузов и дату. Я подготовлю черновик. Публикация — только после вашей проверки и подтверждения.",
      createFreight: "Разместить груз",
      findCarrier: "Найти перевозчика",
      myFreights: "Мои грузы / отклики",
      placeholder: "Например: Ташкент Алматы 18т оборудование, тент",
      draftTitle: "Черновик груза",
      openDraft: "Открыть и проверить",
    };
  }, [language]);
  const expeditorUi = useMemo(() => {
    if (language === "uz") {
      return {
        subtitle: "Экспедитор учун REISOR AI",
        greeting: "Юк топиш ёки бўш машинани эълонга тайёрлаш мумкин. Машина AI орқали тўғридан-тўғри эълон қилинмайди — аввал сиз текширасиз.",
        findFreight: "Юк топиш",
        prepareVehicle: "Машинани тайёрлаш",
        openVehicles: "Бўш машиналар",
        myFreights: "Менинг юкларим",
        placeholder: "Масалан: Москва Тошкент реф 20т",
        vehicleGreeting: "Бўш машина қораламасини тайёрлайман. Қаердан, қаерга, кузов, тоннаж ва қачон бўшлигини ёзинг. Эълон фақат формада текшириб тасдиқлагандан кейин жойланади.",
        vehiclePlaceholder: "Масалан: Москва Тошкент тент 20т, эртага бўш",
        vehiclePromptPrefix: "Бўш машинани эълонга тайёрла: ",
        vehicleDraftTitle: "Бўш машина — қоралама",
        vehicleDraftHint: "AI ҳеч нарсани эълон қилмади. Формани очинг, маълумотларни текширинг ва кейин ўзингиз тасдиқланг.",
        openVehicleDraft: "Очиш ва текшириш",
      };
    }
    if (language === "tr") {
      return {
        subtitle: "Ekspeditör için REISOR AI",
        greeting: "Yük bulabilir veya müsait bir araç için ilan taslağı hazırlayabilirsiniz. AI aracı doğrudan yayınlamaz — önce siz kontrol edersiniz.",
        findFreight: "Yük bul",
        prepareVehicle: "Araç taslağı",
        openVehicles: "Müsait araçlar",
        myFreights: "Yüklerim",
        placeholder: "Örnek: Moskova Taşkent frigorifik 20t",
        vehicleGreeting: "Müsait araç taslağını hazırlayacağım. Nereden, nereye, kasa tipi, tonaj ve ne zaman müsait olduğunu yazın. İlan yalnızca formda kontrol edip onayladıktan sonra yayınlanır.",
        vehiclePlaceholder: "Örnek: Moskova Taşkent tenteli 20t, yarın müsait",
        vehiclePromptPrefix: "Müsait araç ilan taslağı hazırla: ",
        vehicleDraftTitle: "Müsait araç — taslak",
        vehicleDraftHint: "AI hiçbir şeyi yayınlamadı. Formu açın, bilgileri kontrol edin ve ardından kendiniz onaylayın.",
        openVehicleDraft: "Aç ve kontrol et",
      };
    }
    return {
      subtitle: "REISOR AI для экспедитора",
      greeting: "Можно найти груз или подготовить объявление свободной машины. AI сам машину не публикует — сначала вы проверяете данные.",
      findFreight: "Найти груз",
      prepareVehicle: "Оформить машину",
      openVehicles: "Свободные машины",
      myFreights: "Мои грузы",
      placeholder: "Например: Москва Ташкент реф 20т",
      vehicleGreeting: "Подготовлю черновик свободной машины. Напишите откуда, куда, кузов, тоннаж и когда машина свободна. Публикация — только после вашей проверки в форме.",
      vehiclePlaceholder: "Например: Москва Ташкент тент 20т, свободен завтра",
      vehiclePromptPrefix: "Оформить свободную машину: ",
      vehicleDraftTitle: "Свободная машина — черновик",
      vehicleDraftHint: "AI ничего не опубликовал. Откройте форму, проверьте данные и только потом подтвердите публикацию.",
      openVehicleDraft: "Открыть и проверить",
    };
  }, [language]);
  const initial = useMemo(() => readPersisted(driverId), [driverId]);
  const [conversationId, setConversationId] = useState(initial.conversationId);
  const [messages, setMessages] = useState(initial.messages);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [busyResultSetId, setBusyResultSetId] = useState("");
  const [confirmSaveId, setConfirmSaveId] = useState("");
  const [savedState, setSavedState] = useState({});
  const savePromptSessionKey = `reisor_ai_save_prompt_later_${surface}_${String(driverId || "anon")}`;
  const [ownVehicles, setOwnVehicles] = useState([]);
  const [vehicleState, setVehicleState] = useState({});
  const [quota, setQuota] = useState({ limit: 50, used: 0, remaining: 50, exhausted: false, resetsAt: null });
  const [quotaLoaded, setQuotaLoaded] = useState(false);
  const [activeEntryIntent, setActiveEntryIntent] = useState("");
  const scrollerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const next = readPersisted(driverId);
    setConversationId(next.conversationId);
    setMessages(next.messages);
    setErrorText("");
    setConfirmSaveId("");
    setSavedState({});
    setOwnVehicles([]);
    setVehicleState({});
    setQuota({ limit: 50, used: 0, remaining: 50, exhausted: false, resetsAt: null });
    setQuotaLoaded(false);
  }, [driverId]);

  useEffect(() => {
    persistState(driverId, conversationId, messages);
  }, [driverId, conversationId, messages]);

  useEffect(() => {
    if (!open) return;
    const next = String(entryIntent || "").trim().toLowerCase();
    setActiveEntryIntent(next === "vehicle" || next === "freight" ? next : "");
  }, [open, entryIntent]);


  useEffect(() => {
    if (!open || !driverToken) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    getQuotaRequest({ token: driverToken, signal: controller.signal })
      .then((data) => {
        if (cancelled) return;
        if (data?.quota) setQuota(data.quota);
        setQuotaLoaded(true);
      })
      .catch((error) => {
        if (String(error?.name || "") === "AbortError" || cancelled) return;
        setQuotaLoaded(true);
      });
    return () => {
      cancelled = true;
      try { controller.abort(); } catch {}
    };
  }, [open, driverToken, driverId, getQuotaRequest]);

  useEffect(() => {
    if (!open || surface !== "driver" || !driverToken) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    getDriverAvailableVehicles({ token: driverToken, signal: controller.signal })
      .then((data) => { if (!cancelled) setOwnVehicles(Array.isArray(data?.items) ? data.items : []); })
      .catch(() => { if (!cancelled) setOwnVehicles([]); });
    return () => { cancelled = true; try { controller.abort(); } catch {} };
  }, [open, surface, driverToken]);

  useEffect(() => {
    if (!open || !quota?.exhausted || !quota?.resetsAt || !driverToken) return undefined;
    const resetMs = new Date(quota.resetsAt).getTime();
    if (!Number.isFinite(resetMs)) return undefined;
    const delay = Math.max(1000, Math.min(86_400_000, resetMs - Date.now() + 1000));
    const timer = window.setTimeout(() => {
      getQuotaRequest({ token: driverToken })
        .then((data) => { if (data?.quota) setQuota(data.quota); })
        .catch(() => {});
    }, delay);
    return () => window.clearTimeout(timer);
  }, [open, quota?.exhausted, quota?.resetsAt, driverToken, getQuotaRequest]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      try {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      } catch {}
    }, 40);
    return () => window.clearTimeout(timer);
  }, [open, messages, sending, errorText, confirmSaveId]);

  useEffect(() => () => {
    try { abortRef.current?.abort?.(); } catch {}
  }, []);

  async function submit(event) {
    event?.preventDefault?.();
    const text = String(input || "").trim();
    if (!text || sending || quota?.exhausted) return;

    const userRow = { id: makeId("user"), role: "user", text };
    setMessages((prev) => [...prev, userRow]);
    setInput("");
    setErrorText("");
    setSending(true);

    try {
      try { abortRef.current?.abort?.(); } catch {}
      const controller = new AbortController();
      abortRef.current = controller;

      const reply = await sendMessageRequest({
        message: text,
        conversationId,
        token: driverToken,
        signal: controller.signal,
      });

      if (reply?.quota) setQuota(reply.quota);
      const nextConversationId = String(reply?.conversationId || conversationId || "");
      if (nextConversationId) setConversationId(nextConversationId);

      let result = null;
      const resultSetId = String(reply?.resultSetId || "").trim();
      if (resultSetId) {
        result = await getResultSetRequest({
          resultSetId,
          offset: 0,
          limit: RESULT_PAGE_SIZE,
          token: driverToken,
          signal: controller.signal,
        });
      }

      const assistantRow = {
        id: makeId("assistant"),
        role: "assistant",
        text: String(reply?.message || t("reisorAi.emptyReply", { defaultValue: "Готово." })),
        result: result
          ? {
              resultSetId: String(result?.resultSetId || resultSetId),
              searchSpec: result?.searchSpec || reply?.searchSpec || {},
              total: Number(result?.total || 0),
              items: Array.isArray(result?.items) ? result.items : [],
              expiresAt: result?.expiresAt || null,
              returnLoadIntent: Boolean(reply?.returnLoadIntent),
              vehiclePublishIntent: Boolean(reply?.vehiclePublishIntent || reply?.returnLoadIntent),
              sourceText: text,
            }
          : null,
        vehicleDraft: reply?.type === "vehicle_draft" && reply?.vehicleDraft
          ? { ...reply.vehicleDraft, sourceText: text, aiContextId: String(reply?.conversationId || nextConversationId || "") }
          : null,
        freightDraft: reply?.type === "freight_draft" && reply?.freightDraft
          ? { ...reply.freightDraft, sourceText: text, aiContextId: String(reply?.conversationId || nextConversationId || "") }
          : null,
        vehicleResult: reply?.type === "vehicle_result"
          ? {
              total: Number(reply?.total || 0),
              filters: reply?.filters || {},
              items: Array.isArray(reply?.items) ? reply.items : [],
            }
          : null,
      };
      setMessages((prev) => [...prev, assistantRow]);

      if (assistantRow.result && canSaveSearch(assistantRow.result.searchSpec || {})) {
        const criteria = savedSearchCriteria(assistantRow.result.searchSpec || {});
        const rid = String(assistantRow.result.resultSetId || "").trim();
        let suppressed = false;
        try { suppressed = sessionStorage.getItem(savePromptSessionKey) === "1"; } catch {}
        try {
          const check = typeof onCheckSavedSearch === "function"
            ? await onCheckSavedSearch(criteria)
            : await checkDriverSavedFreightSearch({ criteria, token: driverToken, signal: controller.signal });
          if ((check?.exists || check?.item) && check?.notificationsEnabled !== false) {
            setSavedState((prev) => ({ ...prev, [rid]: "saved" }));
          } else if (!suppressed && rid) {
            setConfirmSaveId(rid);
          }
        } catch {
          // Auto-prompt is best-effort. Manual save remains available.
        }
      }
    } catch (error) {
      if (String(error?.name || "") !== "AbortError") {
        if (error?.payload?.quota) setQuota(error.payload.quota);
        setErrorText(friendlyError(error, t));
      }
    } finally {
      setSending(false);
    }
  }

  async function loadMore(rowId, result) {
    const resultSetId = String(result?.resultSetId || "").trim();
    const current = Array.isArray(result?.items) ? result.items : [];
    const total = Number(result?.total || 0);
    if (!resultSetId || busyResultSetId || current.length >= total) return;

    setBusyResultSetId(resultSetId);
    setErrorText("");
    try {
      const next = await getResultSetRequest({
        resultSetId,
        offset: current.length,
        limit: RESULT_PAGE_SIZE,
        token: driverToken,
      });
      const extra = Array.isArray(next?.items) ? next.items : [];
      setMessages((prev) => prev.map((row) => {
        if (row.id !== rowId || !row.result) return row;
        const seen = new Set((row.result.items || []).map((item) => String(item?.id || "")));
        const merged = [
          ...(row.result.items || []),
          ...extra.filter((item) => {
            const id = String(item?.id || "");
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          }),
        ];
        return {
          ...row,
          result: {
            ...row.result,
            total: Number(next?.total ?? row.result.total ?? merged.length),
            items: merged,
          },
        };
      }));
    } catch (error) {
      setErrorText(friendlyError(error, t));
    } finally {
      setBusyResultSetId("");
    }
  }

  async function confirmSave(result) {
    const resultSetId = String(result?.resultSetId || "").trim();
    const criteria = savedSearchCriteria(result?.searchSpec || {});
    if (!resultSetId || !canSaveSearch(result?.searchSpec || {})) return;

    setSavedState((prev) => ({ ...prev, [resultSetId]: "saving" }));
    setErrorText("");
    try {
      if (typeof onSaveSearch === "function") {
        await onSaveSearch(criteria);
      } else {
        await createDriverSavedFreightSearch({ criteria, token: driverToken });
      }
      setSavedState((prev) => ({ ...prev, [resultSetId]: "saved" }));
      setConfirmSaveId("");
      const ahaKey = `reisor_ai_saved_search_aha_v1_${String(driverId || "anon")}`;
      let alreadyShown = false;
      try { alreadyShown = localStorage.getItem(ahaKey) === "1"; } catch {}
      if (!alreadyShown) {
        try { localStorage.setItem(ahaKey, "1"); } catch {}
        setMessages((prev) => [...prev, {
          id: makeId("assistant"),
          role: "assistant",
          text: t("reisorAi.savedSearchAha", { defaultValue: "Готово. Теперь не нужно постоянно проверять ленту — REISOR сообщит, когда по этому маршруту появится новый подходящий груз." }),
        }]);
      }
    } catch (error) {
      const code = String(error?.code || error?.data?.error || "");
      if (code === "SAVED_SEARCH_ALREADY_EXISTS") {
        setSavedState((prev) => ({ ...prev, [resultSetId]: "saved" }));
        setConfirmSaveId("");
        return;
      }
      if (code === "SAVED_SEARCH_LIMIT_REACHED") {
        setErrorText(t("reisorAi.errors.savedSearchLimit", { defaultValue: "Достигнут лимит: до 20 активных уведомлений." }));
      } else {
        setErrorText(friendlyError(error, t));
      }
      setSavedState((prev) => ({ ...prev, [resultSetId]: "error" }));
    }
  }


  async function publishReturnVehicle(result, directDraft = null) {
    const resultSetId = String(result?.resultSetId || "").trim();
    const aiContextId = String(directDraft?.aiContextId || resultSetId || conversationId || "").trim();
    const draft = buildReturnVehicleDraft({
      spec: directDraft || result?.searchSpec || {},
      sourceText: directDraft?.sourceText || result?.sourceText || "",
      driver: driver || {},
      ownVehicles,
    });
    const stateKey = String(resultSetId || aiContextId || directDraft?.id || "vehicle");
    if (draft.duplicate) {
      setVehicleState((prev) => ({ ...prev, [stateKey]: "exists" }));
      return;
    }
    if (!draft.ready) {
      setVehicleState((prev) => ({ ...prev, [stateKey]: "needs_edit" }));
      onPrepareVehicle?.(draft);
      return;
    }
    setVehicleState((prev) => ({ ...prev, [stateKey]: "publishing" }));
    setErrorText("");
    try {
      const payload = {
        from: draft.from,
        to: draft.to,
        bodyType: draft.bodyType,
        capacityTons: draft.capacityTons,
        volumeM3: draft.volumeM3,
        partialLoad: false,
        priceMode: "NEGOTIABLE",
        priceAmount: null,
        currency: "UZS",
        comment: "",
      };
      const data = await createDriverAvailableVehicle({
        vehicle: payload,
        token: driverToken,
        ...(resultSetId ? { aiReturnPublish: true, aiResultSetId: resultSetId } : { aiGuidedPublish: true, aiContextId }),
      });
      if (data?.item) {
        setOwnVehicles((prev) => {
          const next = prev.filter((item) => String(item?.id || "") !== String(data.item?.id || ""));
          return [data.item, ...next];
        });
      }
      setVehicleState((prev) => ({
        ...prev,
        [stateKey]: data?.deduplicated ? "exists" : "published",
      }));
    } catch (error) {
      setVehicleState((prev) => ({ ...prev, [stateKey]: "error" }));
      setErrorText(friendlyError(error, t));
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10050] flex flex-col bg-slate-950 text-white"
      data-reisor-ai-driver-ux="v03d2"
      data-reisor-ai-surface={surface}
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}

        data-reisor-theme-surface="driver-ai">
      <div className="flex items-center gap-3 border-b border-slate-800 bg-slate-950/95 px-3 py-3 backdrop-blur">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 text-slate-100"
          aria-label={t("reisorAi.close", { defaultValue: "Закрыть" })}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600/20 text-blue-300">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-black">REISOR AI</div>
          <div className="truncate text-xs font-semibold text-slate-400">
            {isCargoSurface
              ? cargoUi.subtitle
              : isExpeditorSurface
                ? expeditorUi.subtitle
                : t("reisorAi.subtitle", { defaultValue: "Поиск грузов по обычному сообщению" })}
          </div>
        </div>
        <div
          className="reisor-ai-quota-pill inline-flex h-9 shrink-0 items-center justify-center rounded-full border px-3 text-sm font-black tabular-nums"
          aria-label={`${Number(quota?.used || 0)} / ${Number(quota?.limit || 50)}`}
          title={quota?.resetsAt ? new Date(quota.resetsAt).toLocaleString() : ""}
        >
          {quotaLoaded ? `${Number(quota?.used || 0)}/${Number(quota?.limit || 50)}` : `—/${Number(quota?.limit || 50)}`}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-400"
          aria-label={t("reisorAi.close", { defaultValue: "Закрыть" })}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-3">
          {messages.length === 0 ? (
            <div className="max-w-[96%] self-start space-y-3 rounded-2xl rounded-tl-md border border-slate-700/60 bg-slate-900 px-4 py-3 text-sm leading-relaxed text-slate-100">
              <div>
                {isCargoSurface
                  ? cargoUi.greeting
                  : isExpeditorSurface
                    ? (
                        activeEntryIntent === "vehicle"
                          ? expeditorUi.vehicleGreeting
                          : expeditorUi.greeting
                      )
                    : activeEntryIntent === "vehicle"
                      ? t("reisorAi.vehicleEntryGreeting", { defaultValue: "Оформим свободную машину. Напишите откуда, куда, тип кузова и тоннаж. Например: «Москва Ташкент реф 20т»." })
                      : activeEntryIntent === "freight"
                        ? t("reisorAi.freightEntryGreeting", { defaultValue: "Найдём груз. Напишите маршрут и условия обычными словами. Например: «Москва Ташкент реф 20т»." })
                        : t("reisorAi.greeting", { defaultValue: "Я помогу найти груз или оформить свободную машину. Напишите обычными словами, что нужно." })}
              </div>
              {isCargoSurface ? null : surface === "driver" ? (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setActiveEntryIntent("freight")} className="min-h-[42px] rounded-xl bg-blue-600 px-3 text-xs font-black text-white">
                    {t("reisorAi.launcherFindFreight", { defaultValue: "Найти груз" })}
                  </button>
                  <button type="button" onClick={() => setActiveEntryIntent("vehicle")} className="min-h-[42px] rounded-xl bg-emerald-600 px-3 text-xs font-black text-white">
                    {t("reisorAi.launcherPublishVehicle", { defaultValue: "Оформить свободную машину" })}
                  </button>
                </div>
              ) : isExpeditorSurface ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveEntryIntent(
                        "freight"
                      )
                    }
                    className="min-h-[42px] rounded-xl bg-blue-600 px-3 text-xs font-black text-white"
                  >
                    {expeditorUi.findFreight}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setActiveEntryIntent(
                        "vehicle"
                      );

                      setInput(
                        (current) =>
                          String(
                            current || ""
                          ).trim()
                            ? current
                            : expeditorUi.vehiclePromptPrefix
                      );
                    }}
                    className="min-h-[42px] rounded-xl bg-emerald-600 px-3 text-xs font-black text-white"
                  >
                    {expeditorUi.prepareVehicle}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onOpenVehicleMarket?.()
                    }
                    className="min-h-[42px] rounded-xl bg-slate-800 px-3 text-xs font-black text-white"
                  >
                    {expeditorUi.openVehicles}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onOpenMyFreights?.()
                    }
                    className="min-h-[42px] rounded-xl bg-slate-800 px-3 text-xs font-black text-white"
                  >
                    {expeditorUi.myFreights}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {messages.map((row) => {
            const result = row?.result || null;
            const resultSetId = String(result?.resultSetId || "");
            const spec = result?.searchSpec || {};
            const savePossible = canSaveSearch(spec);
            const saveStatus = savedState[resultSetId] || "idle";
            const returnDraft = result?.vehiclePublishIntent && surface === "driver"
              ? buildReturnVehicleDraft({ spec, sourceText: result?.sourceText || "", driver: driver || {}, ownVehicles })
              : null;
            const returnVehicleStatus = vehicleState[resultSetId] || "idle";
            return (
              <div key={row.id} className={row.role === "user" ? "self-end max-w-[92%]" : "self-start w-full max-w-[96%]"}>
                <div
                  className={row.role === "user"
                    ? "reisor-ai-driver-user-bubble rounded-2xl rounded-tr-md bg-blue-600 px-4 py-3 text-sm font-semibold leading-relaxed text-white"
                    : "rounded-2xl rounded-tl-md border border-slate-700/60 bg-slate-900 px-4 py-3 text-sm leading-relaxed text-slate-100"}
                >
                  {row.text}
                </div>

                {row.role === "assistant" && result ? (
                  <div className="mt-2 space-y-2">
                    {(result.items || []).map((item) => (
                      <FreightPreviewCard
                        key={String(item?.id || makeId("freight"))}
                        item={item}
                        language={language}
                        onOpen={(freightId) => onOpenFreight?.(freightId)}
                      />
                    ))}

                    {Number(result.total || 0) > (result.items || []).length ? (
                      <button
                        type="button"
                        disabled={busyResultSetId === resultSetId}
                        onClick={() => loadMore(row.id, result)}
                        className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 text-sm font-black text-slate-200 disabled:opacity-60"
                      >
                        {busyResultSetId === resultSetId ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        {t("reisorAi.showMore", { defaultValue: "Показать ещё" })} · {Math.max(0, Number(result.total || 0) - (result.items || []).length)}
                      </button>
                    ) : null}

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => onShowInMarket?.(spec)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 text-sm font-black text-white active:bg-emerald-700"
                      >
                        <Search className="h-4 w-4" />
                        {t("reisorAi.showInMarket", { defaultValue: "В рынок" })}
                      </button>
                      <button
                        type="button"
                        disabled={!savePossible || saveStatus === "saving" || saveStatus === "saved"}
                        onClick={() => setConfirmSaveId(resultSetId)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {saveStatus === "saving" ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                        {saveStatus === "saved"
                          ? t("reisorAi.saved", { defaultValue: "Сохранено" })
                          : t("reisorAi.saveNotify", { defaultValue: "Сохранить" })}
                      </button>
                    </div>

                    {!savePossible ? (
                      <div className="rounded-xl border border-amber-700/40 bg-amber-950/25 px-3 py-2 text-xs font-semibold text-amber-200">
                        {t("reisorAi.saveRequiresRouteTruck", { defaultValue: "Для уведомления нужны откуда, куда и тип кузова." })}
                      </div>
                    ) : null}

                    {confirmSaveId === resultSetId ? (
                      <div className="fixed inset-0 z-[10080] flex items-center justify-center bg-black/55 px-4" role="dialog" aria-modal="true">
                        <div className="w-full max-w-[420px] rounded-3xl border border-blue-500/30 bg-slate-950 p-4 shadow-2xl">
                        <div className="text-sm font-black text-white">
                          {t("reisorAi.confirmSaveTitle", { defaultValue: "Сохранить этот поиск?" })}
                        </div>
                        <div className="mt-1 text-xs font-semibold leading-relaxed text-slate-300">
                          {t("reisorAi.confirmSaveDescription", { defaultValue: "Получайте уведомления, когда по этому направлению появятся свежие грузы." })}
                        </div>
                        <div className="mt-2 text-xs font-semibold text-blue-100">
                          {`${String(spec?.from || "")} → ${String(spec?.to || "")} · ${String(spec?.truck || "")}`}
                        </div>
                        {savedSearchDropsExtraFilters(spec) ? (
                          <div className="mt-2 text-[11px] leading-relaxed text-slate-300">
                            {t("reisorAi.savedSearchSubsetHint", { defaultValue: "Уведомление сохраняет маршрут и кузов. Дата, цена, вес и свежесть остаются только в AI-поиске." })}
                          </div>
                        ) : null}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              try { sessionStorage.setItem(savePromptSessionKey, "1"); } catch {}
                              setConfirmSaveId("");
                            }}
                            className="min-h-[40px] rounded-xl bg-slate-800 px-3 text-sm font-bold text-slate-200"
                          >
                            {t("reisorAi.later", { defaultValue: "Позже" })}
                          </button>
                          <button
                            type="button"
                            disabled={saveStatus === "saving"}
                            onClick={() => confirmSave(result)}
                            className="min-h-[40px] rounded-xl bg-blue-600 px-3 text-sm font-black text-white disabled:opacity-60"
                          >
                            {t("reisorAi.confirmSaveAction", { defaultValue: "Сохранить и получать уведомления" })}
                          </button>
                        </div>
                        </div>
                      </div>
                    ) : null}

                    {returnDraft ? (
                      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/25 p-3">
                        <div className="flex items-start gap-2">
                          <Truck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-black text-white">
                              {t("reisorAi.returnVehicleTitle", { defaultValue: "Свободная машина — черновик" })}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-emerald-100">
                              {`${returnDraft.from || "—"} → ${returnDraft.to || "—"}`}
                            </div>
                            <div className="mt-2 text-xs leading-relaxed text-slate-300">
                              {t("reisorAi.returnVehicleSaveSearchHint", { defaultValue: "После подтверждения машина публикуется сразу на 24 часа. За 15 минут до окончания придёт напоминание." })}
                            </div>
                            <div className="mt-2 text-xs text-slate-300">
                              {t("reisorAi.returnVehicleBody", { defaultValue: "Кузов" })}: <b>{returnDraft.bodyType || "—"}</b>
                              {" · "}{t("reisorAi.returnVehicleCapacity", { defaultValue: "Тоннаж" })}: <b>{returnDraft.capacityTons || "—"}</b>
                            </div>
                          </div>
                        </div>

                        {returnDraft.duplicate || returnVehicleStatus === "exists" ? (
                          <div className="mt-3 rounded-xl border border-amber-600/30 bg-amber-950/25 px-3 py-2 text-xs font-bold text-amber-200">
                            {t("reisorAi.returnVehicleAlreadyPublished", { defaultValue: "Похожая активная машина уже опубликована — дубль не создаём." })}
                          </div>
                        ) : null}

                        {returnVehicleStatus === "published" ? (
                          <div className="mt-3 inline-flex items-center gap-2 text-xs font-black text-emerald-300">
                            <CheckCircle2 className="h-4 w-4" />
                            {t("reisorAi.returnVehiclePublished", { defaultValue: "Машина опубликована" })}
                          </div>
                        ) : null}

                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => onPrepareVehicle?.(returnDraft.duplicate || returnDraft)}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-slate-800 px-3 text-sm font-bold text-slate-100"
                          >
                            <Pencil className="h-4 w-4" />
                            {t("reisorAi.returnVehicleEdit", { defaultValue: "Изменить" })}
                          </button>
                          <button
                            type="button"
                            disabled={Boolean(returnDraft.duplicate) || returnVehicleStatus === "publishing" || returnVehicleStatus === "published"}
                            onClick={() => publishReturnVehicle(result)}
                            className="min-h-[42px] rounded-xl bg-emerald-600 px-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {returnVehicleStatus === "publishing"
                              ? t("reisorAi.returnVehiclePublishing", { defaultValue: "Публикуем…" })
                              : returnDraft.ready
                                ? t("reisorAi.returnVehicleConfirmPublish", { defaultValue: "Подтвердить и опубликовать" })
                                : t("reisorAi.returnVehicleComplete", { defaultValue: "Заполнить и опубликовать" })}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {row.role === "assistant" && row?.vehicleResult ? (() => {
                  const vr = row.vehicleResult || {};
                  const rows = Array.isArray(vr.items) ? vr.items : [];

                  return (
                    <div
                      className="mt-2 space-y-2 rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-3"
                      data-reisor-ai-vehicle-result="v1"
                    >
                      {rows.map((item) => {
                        const route =
                          `${String(item?.from || "—")} → ${String(item?.to || "—")}`;

                        const body =
                          String(
                            item?.bodyTypeOther ||
                            item?.bodyType ||
                            "—"
                          );

                        const tons =
                          Number(
                            item?.capacityTons
                          );

                        return (
                          <div
                            key={String(item?.id || makeId("vehicle"))}
                            className="rounded-xl border border-slate-700/70 bg-slate-900/85 px-3 py-2.5"
                          >
                            <div className="text-sm font-black text-white">
                              {route}
                            </div>

                            <div className="mt-1 text-xs font-semibold text-slate-300">
                              {body}
                              {Number.isFinite(tons) && tons > 0
                                ? ` · ${tons} т`
                                : ""}
                            </div>
                          </div>
                        );
                      })}

                      <button
                        type="button"
                        onClick={() =>
                          onOpenVehicleMarket?.(
                            vr.filters || {}
                          )
                        }
                        className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 text-sm font-black text-white active:bg-emerald-700"
                      >
                        <Truck className="h-4 w-4" />

                        {t("reisorAi.openVehicleMarket", {
                          defaultValue: "Открыть рынок машин",
                        })}
                      </button>
                    </div>
                  );
                })() : null}

                {row.role === "assistant" && row?.freightDraft && isCargoSurface ? (() => {
                  const draft = row.freightDraft || {};
                  const route = `${String(draft.from || "—")} → ${String(draft.to || "—")}`;
                  const weight = draft.weightTons == null || draft.weightTons === "" ? null : Number(draft.weightTons);
                  const volume = draft.volumeM3 == null || draft.volumeM3 === "" ? null : Number(draft.volumeM3);
                  return (
                    <div className="mt-2 rounded-2xl border border-blue-500/30 bg-blue-950/25 p-3">
                      <div className="text-sm font-black text-white">{cargoUi.draftTitle}</div>
                      <div className="mt-1 text-xs font-semibold text-blue-100">{route}</div>
                      <div className="mt-2 text-xs leading-relaxed text-slate-300">
                        {draft.cargo ? <span>{String(draft.cargo)}</span> : null}
                        {Number.isFinite(weight) && weight > 0 ? <span>{draft.cargo ? " · " : ""}{weight} т</span> : null}
                        {draft.truck ? <span>{(draft.cargo || (Number.isFinite(weight) && weight > 0)) ? " · " : ""}{String(draft.truck)}</span> : null}
                        {Number.isFinite(volume) && volume >= 0 ? <span>{" · "}{volume} м³</span> : null}
                        {draft.loadingDate ? <span>{" · "}{String(draft.loadingDate)}</span> : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => onPrepareFreight?.(draft)}
                        className="mt-3 inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-sm font-black text-white"
                      >
                        <Pencil className="h-4 w-4" />
                        {cargoUi.openDraft}
                      </button>
                    </div>
                  );
                })() : null}

                {row.role === "assistant" && row?.vehicleDraft && isExpeditorSurface ? (() => {
                  const draft =
                    row.vehicleDraft || {};

                  const bodyType =
                    String(
                      draft?.truck ||
                      draft?.bodyType ||
                      ""
                    ).trim();

                  const rawCapacity =
                    draft?.capacityTons;

                  const parsedCapacity =
                    rawCapacity == null ||
                    rawCapacity === ""
                      ? null
                      : Number(rawCapacity);

                  const capacityTons =
                    Number.isFinite(
                      parsedCapacity
                    ) &&
                    parsedCapacity > 0
                      ? parsedCapacity
                      : null;

                  const rawVolume =
                    draft?.volumeM3;

                  const parsedVolume =
                    rawVolume == null ||
                    rawVolume === ""
                      ? null
                      : Number(rawVolume);

                  const volumeM3 =
                    Number.isFinite(
                      parsedVolume
                    ) &&
                    parsedVolume >= 0
                      ? parsedVolume
                      : null;

                  const availableFrom =
                    String(
                      draft?.availableFrom ||
                      ""
                    ).trim();

                  return (
                    <div
                      className="mt-2 rounded-2xl border border-emerald-500/30 bg-emerald-950/25 p-3"
                      data-reisor-ai-expeditor-vehicle-draft="v1"
                    >
                      <div className="flex items-start gap-2">
                        <Truck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-black text-white">
                            {expeditorUi.vehicleDraftTitle}
                          </div>

                          <div className="mt-1 text-xs font-semibold text-emerald-100">
                            {`${String(draft?.from || "—")} → ${String(draft?.to || "—")}`}
                          </div>

                          <div className="mt-2 text-xs leading-relaxed text-slate-300">
                            {expeditorUi.vehicleDraftHint}
                          </div>

                          <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-300">
                            {bodyType ? (
                              <span>
                                {t("reisorAi.returnVehicleBody", { defaultValue: "Кузов" })}: <b>{bodyType}</b>
                              </span>
                            ) : null}

                            {capacityTons != null ? (
                              <span>
                                {t("reisorAi.returnVehicleCapacity", { defaultValue: "Тоннаж" })}: <b>{capacityTons} т</b>
                              </span>
                            ) : null}

                            {volumeM3 != null ? (
                              <span>
                                {t("freightMarket.volume", { defaultValue: "Объём" })}: <b>{volumeM3} м³</b>
                              </span>
                            ) : null}

                            {availableFrom ? (
                              <span>
                                {t("reisorAi.availableFrom", { defaultValue: "Свободна" })}: <b>{availableFrom}</b>
                              </span>
                            ) : null}

                            {draft?.partialLoad ? (
                              <span>
                                {t("reisorAi.partialLoad", { defaultValue: "Догруз" })}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          onPrepareVehicle?.({
                            aiDraft: true,

                            from:
                              String(
                                draft?.from ||
                                ""
                              ).trim(),

                            to:
                              String(
                                draft?.to ||
                                ""
                              ).trim(),

                            bodyType,

                            capacityTons,

                            volumeM3,

                            partialLoad:
                              Boolean(
                                draft?.partialLoad
                              ),

                            availableFrom,
                          })
                        }
                        className="mt-3 inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 text-sm font-black text-white"
                      >
                        <Pencil className="h-4 w-4" />
                        {expeditorUi.openVehicleDraft}
                      </button>
                    </div>
                  );
                })() : null}

                {row.role === "assistant" && row?.vehicleDraft && surface === "driver" ? (() => {
                  const draft = buildReturnVehicleDraft({
                    spec: row.vehicleDraft,
                    sourceText: row.vehicleDraft?.sourceText || "",
                    driver: driver || {},
                    ownVehicles,
                  });
                  const directStateKey = String(row.vehicleDraft?.aiContextId || row.id || "vehicle");
                  const directStatus = vehicleState[directStateKey] || "idle";
                  return (
                    <div className="mt-2 rounded-2xl border border-emerald-500/30 bg-emerald-950/25 p-3">
                      <div className="flex items-start gap-2">
                        <Truck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-black text-white">{t("reisorAi.returnVehicleTitle", { defaultValue: "Свободная машина — черновик" })}</div>
                          <div className="mt-1 text-xs font-semibold text-emerald-100">{`${draft.from || "—"} → ${draft.to || "—"}`}</div>
                          <div className="mt-2 text-xs leading-relaxed text-slate-300">{t("reisorAi.returnVehicleSaveSearchHint", { defaultValue: "После подтверждения машина публикуется сразу на 24 часа. За 15 минут до окончания придёт напоминание." })}</div>
                          <div className="mt-2 text-xs text-slate-300">
                            {t("reisorAi.returnVehicleBody", { defaultValue: "Кузов" })}: <b>{draft.bodyType || "—"}</b>
                            {" · "}{t("reisorAi.returnVehicleCapacity", { defaultValue: "Тоннаж" })}: <b>{draft.capacityTons || "—"}</b>
                          </div>
                        </div>
                      </div>
                      {draft.duplicate || directStatus === "exists" ? (
                        <div className="mt-3 rounded-xl border border-amber-600/30 bg-amber-950/25 px-3 py-2 text-xs font-bold text-amber-200">
                          {t("reisorAi.returnVehicleAlreadyPublished", { defaultValue: "Похожая активная машина уже опубликована — дубль не создаём." })}
                        </div>
                      ) : null}
                      {directStatus === "published" ? (
                        <div className="mt-3 inline-flex items-center gap-2 text-xs font-black text-emerald-300">
                          <CheckCircle2 className="h-4 w-4" />
                          {t("reisorAi.returnVehiclePublished", { defaultValue: "Машина опубликована" })}
                        </div>
                      ) : null}
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => onPrepareVehicle?.(draft.duplicate || draft)} className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-slate-800 px-3 text-sm font-bold text-slate-100">
                          <Pencil className="h-4 w-4" />
                          {t("reisorAi.returnVehicleEdit", { defaultValue: "Изменить" })}
                        </button>
                        <button type="button" disabled={Boolean(draft.duplicate) || directStatus === "publishing" || directStatus === "published"} onClick={() => publishReturnVehicle(null, { ...row.vehicleDraft, id: row.id })} className="min-h-[42px] rounded-xl bg-emerald-600 px-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50">
                          {directStatus === "publishing"
                            ? t("reisorAi.returnVehiclePublishing", { defaultValue: "Публикуем…" })
                            : draft.ready
                              ? t("reisorAi.returnVehicleConfirmPublish", { defaultValue: "Подтвердить и опубликовать" })
                              : t("reisorAi.returnVehicleComplete", { defaultValue: "Заполнить и опубликовать" })}
                        </button>
                      </div>
                    </div>
                  );
                })() : null}
              </div>
            );
          })}

          {sending ? (
            <div className="inline-flex max-w-[92%] items-center gap-2 self-start rounded-2xl rounded-tl-md border border-slate-700/60 bg-slate-900 px-4 py-3 text-sm font-semibold text-slate-300">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              {t("reisorAi.thinking", { defaultValue: "Думаю…" })}
            </div>
          ) : null}

          {errorText ? (
            <div className="rounded-2xl border border-red-800/50 bg-red-950/30 px-4 py-3 text-sm font-semibold text-red-200">
              {errorText}
            </div>
          ) : null}
        </div>
      </div>

      {quota?.exhausted ? (
        <div className="reisor-ai-quota-note mx-3 mb-0 rounded-t-xl border border-b-0 px-3 py-2 text-center text-xs font-bold">
          {t("reisorAi.errors.dailyLimit", { defaultValue: "Лимит 50/50 исчерпан. Следующие 50 вопросов будут доступны через 24 часа." })}
        </div>
      ) : null}
      <form onSubmit={submit} className="border-t border-slate-800 bg-slate-950/98 px-3 py-3">
        <div className="mx-auto flex w-full max-w-[760px] items-end gap-2">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            rows={1}
            maxLength={2000}
            placeholder={isCargoSurface
              ? cargoUi.placeholder
              : isExpeditorSurface
                ? (
                    activeEntryIntent === "vehicle"
                      ? expeditorUi.vehiclePlaceholder
                      : expeditorUi.placeholder
                  )
                : activeEntryIntent === "vehicle"
                ? t("reisorAi.vehiclePlaceholder", { defaultValue: "Например: Москва Ташкент реф 20т" })
                : t("reisorAi.placeholder", { defaultValue: "Например: Ташкент Москва тент" })}
            disabled={Boolean(quota?.exhausted)}
            className="max-h-32 min-h-[46px] flex-1 resize-none rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-[12px] placeholder:font-semibold placeholder:text-slate-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || quota?.exhausted || !String(input || "").trim()}
            className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white disabled:cursor-not-allowed disabled:opacity-45"
            aria-label={t("reisorAi.send", { defaultValue: "Отправить" })}
          >
            {sending ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </form>
    </div>
  );
}
